/**
 * The microphone and the camera a visitor turns on at a room's threshold, held
 * until they either enter or turn back.
 *
 * Nothing before the room opens a device on its own account (#504): the
 * threshold's switches are the first unmute and the first press of the camera,
 * pressed where the visitor is deciding how to arrive. What they open is not
 * closed and reopened on the way in — a browser holding a one-time grant could
 * ask again once the device was released, and the camera light would blink —
 * so it is handed to the room session, which owns it from then on.
 *
 * The rule this module exists for is that a capture has exactly one owner at a
 * time. While the threshold is up it is this; after `handOver` it is the room;
 * and whatever is still held here when the threshold goes away is stopped.
 */

import type { LastMedia } from "@/engine/state/lastMedia";
import { audioFailureFor, type AudioFailure } from "./mediaErrors";
import { VOICE_CAPTURE } from "./voiceCleaner";

/** The two devices a threshold offers a switch for. */
export type ThresholdDevice = "microphone" | "camera";

/** What a room session is handed at the way in: the captures left on. */
export interface HandedCapture {
  microphone: MediaStream | null;
  camera: MediaStream | null;
}

/** How turning a switch on ended. */
export type SwitchOutcome =
  | { on: true; stream: MediaStream }
  /** The switch stays off. `failure` is why, where the browser said. */
  | { on: false; failure: AudioFailure | null; superseded?: never }
  /**
   * A later request — turned off, turned on again, another device chosen, or
   * the threshold gone — overtook this one before the device answered. It says
   * nothing about the switch: the request that overtook it decides, and taking
   * this for a failure would leave a switch drawn off over a capture still held.
   */
  | { on: false; failure: null; superseded: true };

export interface ThresholdCaptureDeps {
  /** Test seam: open a capture device. */
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
}

export interface ThresholdCapture {
  /** Opens the device, the remembered one where it is given, and holds it. */
  turnOn(
    device: ThresholdDevice,
    deviceId: string | null,
  ): Promise<SwitchOutcome>;
  /** Stops the device and forgets it; a request still opening is overtaken. */
  turnOff(device: ThresholdDevice): void;
  /** What is held for a device right now. */
  stream(device: ThresholdDevice): MediaStream | null;
  /** Gives what is held to the caller and holds nothing afterwards. */
  handOver(): HandedCapture;
  /** Stops whatever is still held. Safe after a hand-over, which left nothing. */
  dispose(): void;
}

/**
 * The attempts for one device, in the order they are tried.
 *
 * The same ladder the room gives both devices: a remembered device that is
 * busy or renamed still ends in a working one rather than a switch that does
 * nothing.
 */
function attemptsFor(
  device: ThresholdDevice,
  deviceId: string | null,
): MediaStreamConstraints[] {
  if (device === "microphone") {
    const preferred = deviceId
      ? [{ audio: { deviceId: { exact: deviceId }, ...VOICE_CAPTURE } }]
      : [];
    return [...preferred, { audio: VOICE_CAPTURE }, { audio: true }];
  }
  const shape: MediaTrackConstraints = {
    width: { ideal: 640 },
    height: { ideal: 480 },
    facingMode: "user",
  };
  const preferred = deviceId
    ? [{ video: { ...shape, deviceId: { exact: deviceId } }, audio: false }]
    : [];
  return [
    ...preferred,
    { video: shape, audio: false },
    { video: true, audio: false },
  ];
}

const isRefusal = (error: unknown) =>
  error instanceof Error &&
  (error.name === "NotAllowedError" || error.name === "SecurityError");

export function createThresholdCapture(
  deps: ThresholdCaptureDeps = {},
): ThresholdCapture {
  const open =
    deps.getUserMedia ??
    ((constraints: MediaStreamConstraints) =>
      navigator.mediaDevices.getUserMedia(constraints));

  const held: Record<ThresholdDevice, MediaStream | null> = {
    microphone: null,
    camera: null,
  };
  /** Which request for each device is the current one; older ones are dropped. */
  const current: Record<ThresholdDevice, number> = {
    microphone: 0,
    camera: 0,
  };

  const release = (device: ThresholdDevice) => {
    held[device]?.getTracks().forEach((track) => track.stop());
    held[device] = null;
  };

  /**
   * Tries each attempt until one opens, stopping early on a refusal or once a
   * later request has overtaken this one: a fallback nobody waits on would
   * open a device only to stop it.
   */
  const openFirst = async (
    attempts: MediaStreamConstraints[],
    overtaken: () => boolean,
  ): Promise<{ stream: MediaStream } | { error: unknown }> => {
    let error: unknown = null;
    for (const constraints of attempts) {
      if (overtaken()) break;
      try {
        return { stream: await open(constraints) };
      } catch (caught) {
        error = caught;
        // A refusal is the visitor's to lift; asking again buys nothing.
        if (isRefusal(caught)) break;
      }
    }
    return { error };
  };

  return {
    async turnOn(device, deviceId) {
      release(device);
      const request = (current[device] += 1);
      const opened = await openFirst(
        attemptsFor(device, deviceId),
        () => request !== current[device],
      );

      // Turned off, turned on again, or the threshold went away while the
      // device was opening: what it opened belongs to nobody.
      if (request !== current[device]) {
        if ("stream" in opened)
          opened.stream.getTracks().forEach((track) => track.stop());
        return { on: false, failure: null, superseded: true };
      }
      if ("error" in opened) {
        return {
          on: false,
          failure: audioFailureFor(
            opened.error instanceof Error ? opened.error : null,
          ),
        };
      }
      held[device] = opened.stream;
      return { on: true, stream: opened.stream };
    },

    turnOff(device) {
      current[device] += 1;
      release(device);
    },

    stream(device) {
      return held[device];
    },

    handOver() {
      const handed = { microphone: held.microphone, camera: held.camera };
      held.microphone = null;
      held.camera = null;
      current.microphone += 1;
      current.camera += 1;
      return handed;
    },

    dispose() {
      current.microphone += 1;
      current.camera += 1;
      release("microphone");
      release("camera");
    },
  };
}

/** Stops everything a hand-over carried, for a walk in that never arrived. */
export function releaseHandedCapture(handed: HandedCapture | null | undefined) {
  handed?.microphone?.getTracks().forEach((track) => track.stop());
  handed?.camera?.getTracks().forEach((track) => track.stop());
}

/**
 * Turns on what was on when the visitor last switched it in a room: the way in
 * of a signed-in visitor, who passes no threshold to switch anything at. A
 * device that will not open stays off, as a refused switch would.
 */
export async function reopenAsLastLeft(
  capture: ThresholdCapture,
  last: LastMedia,
): Promise<void> {
  await Promise.all([
    last.mic ? capture.turnOn("microphone", null) : null,
    last.cam ? capture.turnOn("camera", null) : null,
  ]);
}
